const PERCENT_STYLE: Intl.NumberFormatOptions = { style: 'percent' };

// TODO get the language from back-end of saved local;
const getLanguageKey = (): string =>
  (navigator.language ?? '').toLocaleLowerCase();

/**
 * Gets a new Intl.NumberFormatter for the current language
 */
export const getFormatter = (
  options: Intl.NumberFormatOptions = {},
  precision = 0,
): Intl.NumberFormat =>
  new Intl.NumberFormat(getLanguageKey(), {
    ...options,
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });

/**
 * Converts the supplied floating point whole percentage (0.0 - 100.0) to
 * a display string that is rounded to the nearest whole number, or
 * shown as a 'less than' range for values below 1%.
 * e.g. 0.00068 =>  "< 0.001%"
 * @param percentValue - floating point number between 0 and 100
 * @return string representation for display purposes
 */
export const getPercentageDisplayString = (percentValue: number): string => {
  let formatter = getFormatter(PERCENT_STYLE);
  const percent = percentValue / 100.0;
  if (percent > 1.0) {
    return formatter.format(1);
  }
  if (percent <= 0) {
    return formatter.format(0);
  }
  if (percent < 0.01) {
    let range = `< ${getFormatter(PERCENT_STYLE, 0).format(0.01)}`;
    [1, 2].forEach((digitsOfPrecision) => {
      const fraction = 0.01 / 10 ** digitsOfPrecision;
      if (percent < fraction) {
        formatter = getFormatter(PERCENT_STYLE, digitsOfPrecision);
        range = `< ${formatter.format(fraction)}`;
      }
    });
    return range;
  }
  return formatter.format(percent);
};

/**
 * Converts the supplied floating point number into a locale-formatted string with the specified
 * number of decimal places
 * @param precision {number} - fixed number of decimal places
 * @param value {number} - input floating point value to format
 * @return string representation of value with specified number of decimal places or undefined for invalid input
 */
export const getFixedDecimal = (
  precision: number,
  value?: number,
): string | undefined => {
  if (typeof value === 'number') {
    return getFormatter({}, precision).format(value);
  }
  return undefined;
};
